import React, { useState } from "react";
import { Plus, Check } from "lucide-react";
import { MoneyInput, PctInput, TextInput, FieldLabel } from "./inputs";
import { formatMoney, formatCompact } from "./format";

const uid = () => Math.random().toString(36).slice(2, 10);

export function computePortfolio(investments) {
    const total = investments.reduce((s, r) => s + (Number(r.amount) || 0), 0);
    const rows = investments.map((r) => {
        const amt = Number(r.amount) || 0;
        return { ...r, allocation: total > 0 ? (amt / total) * 100 : 0 };
    });
    const weighted = rows.reduce((s, r) => s + (r.allocation * (Number(r.expected_return) || 0)) / 100, 0);
    return { total, rows, weighted };
}

const byName = (key) => (a, b) => (a[key] || "").localeCompare(b[key] || "");

const Chip = ({ selected, name, onClick, testId }) => (
    <button
        type="button" onClick={onClick}
        className={`inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border text-[13px] font-body transition-all ${selected ? "bg-oth-blue text-white border-oth-blue" : "bg-white border-oth-line text-oth-ink2 hover:border-oth-blue"}`}
        data-testid={testId}
    >
        {selected && <Check size={13} />}
        {name}
    </button>
);

export default function StepInvestments({ categories, investments, setInvestments, returnOverrides }) {
    const [customDraft, setCustomDraft] = useState(null);
    const selectedIds = new Set(investments.filter((r) => !r.is_custom).map((r) => r.subcategory_id));
    const { total, rows } = computePortfolio(investments);

    const resolvedReturn = (subId, adminDefault) => {
        const ov = returnOverrides?.[subId];
        return ov === undefined || ov === "" ? adminDefault : Number(ov);
    };

    const toggle = (cat, sub) => {
        if (selectedIds.has(sub.id)) {
            setInvestments(investments.filter((r) => r.subcategory_id !== sub.id));
        } else {
            setInvestments([...investments, {
                subcategory_id: sub.id, subcategory_name: sub.name,
                category_id: cat.id, category_name: cat.name,
                expected_return: resolvedReturn(sub.id, sub.expected_return), amount: "",
            }]);
        }
    };

    const addCustom = (cat) => {
        if (!customDraft?.name?.trim()) return;
        setInvestments([...investments, {
            subcategory_id: `custom-${uid()}`, subcategory_name: customDraft.name.trim(),
            category_id: cat.id, category_name: cat.name,
            expected_return: Number(customDraft.expected_return) || 8, amount: "", is_custom: true,
        }]);
        setCustomDraft(null);
    };

    const setRow = (id, patch) => setInvestments(investments.map((r) => (r.subcategory_id === id ? { ...r, ...patch } : r)));

    // Group selected rows under their category (config order), sub-categories A→Z.
    const knownCatIds = categories.map((c) => c.id);
    const groups = categories
        .map((cat) => ({ cat, items: rows.filter((r) => r.category_id === cat.id).sort(byName("subcategory_name")) }))
        .filter((g) => g.items.length > 0);
    const orphans = rows.filter((r) => !knownCatIds.includes(r.category_id)).sort(byName("subcategory_name"));
    if (orphans.length) groups.push({ cat: { id: "_other", name: orphans[0].category_name || "Other" }, items: orphans });

    return (
        <div className="space-y-6" data-testid="planner-step-investments">
            <div className="rounded-3xl border border-oth-line/70 bg-white p-6 md:p-8">
                <h3 className="font-display text-lg font-semibold tracking-tight">Select what you currently own</h3>
                <p className="text-sm text-oth-ink3 font-body mt-1 mb-6">Pick every asset type in your portfolio. You'll enter amounts next — allocation is calculated automatically.</p>
                <div className="space-y-6">
                    {categories.map((cat) => (
                        <div key={cat.id}>
                            <div className="text-[11px] uppercase tracking-[0.22em] text-oth-ink3 font-body mb-2.5">{cat.name}</div>
                            <div className="flex flex-wrap gap-2">
                                {[...cat.subcategories].sort(byName("name")).map((sub) => (
                                    <Chip
                                        key={sub.id} selected={selectedIds.has(sub.id)} name={sub.name}
                                        onClick={() => toggle(cat, sub)}
                                        testId={`asset-chip-${sub.name.replace(/\s+/g, "-").toLowerCase()}`}
                                    />
                                ))}
                                {investments.filter((r) => r.is_custom && r.category_id === cat.id).map((r) => (
                                    <Chip
                                        key={r.subcategory_id} selected name={r.subcategory_name}
                                        onClick={() => setInvestments(investments.filter((x) => x.subcategory_id !== r.subcategory_id))}
                                        testId={`asset-chip-custom-${r.subcategory_id}`}
                                    />
                                ))}
                                {cat.allow_custom && (
                                    customDraft ? (
                                        <span className="inline-flex items-center gap-2 flex-wrap">
                                            <span className="w-44"><TextInput value={customDraft.name} onChange={(v) => setCustomDraft({ ...customDraft, name: v })} placeholder="e.g. Vintage cars" testId="custom-asset-name" /></span>
                                            <span className="w-28"><PctInput value={customDraft.expected_return} onChange={(v) => setCustomDraft({ ...customDraft, expected_return: v })} placeholder="8" testId="custom-asset-return" /></span>
                                            <span className="text-[11px] text-oth-ink3 font-body">expected growth p.a.</span>
                                            <button type="button" onClick={() => addCustom(cat)} className="btn-pill btn-pill-blue text-xs px-3 py-2" data-testid="custom-asset-add">Add</button>
                                            <button type="button" onClick={() => setCustomDraft(null)} className="text-xs text-oth-ink3 font-body">Cancel</button>
                                        </span>
                                    ) : (
                                        <button
                                            type="button" onClick={() => setCustomDraft({ name: "", expected_return: 8 })}
                                            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full border border-dashed border-oth-line text-[13px] font-body text-oth-blue hover:border-oth-blue transition-colors"
                                            data-testid="add-custom-asset-btn"
                                        ><Plus size={13} /> Add your own</button>
                                    )
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </div>

            {investments.length > 0 && (
                <div className="rounded-3xl border border-oth-line/70 bg-white p-6 md:p-8" data-testid="investment-table-card">
                    <div className="flex items-center gap-1.5 mb-1">
                        <h3 className="font-display text-lg font-semibold tracking-tight">Enter amounts invested</h3>
                        <FieldLabel tip="Allocation % = each amount ÷ total portfolio × 100. It recalculates instantly as you type — no need to enter percentages yourself."><span /></FieldLabel>
                    </div>
                    <p className="text-sm text-oth-ink3 font-body mb-5">Grouped by category. Allocation updates as you type.</p>
                    <div className="overflow-x-auto thin-scroll">
                        <table className="w-full min-w-[560px] text-sm font-body" data-testid="investment-table">
                            <thead>
                                <tr className="text-left text-[11px] uppercase tracking-[0.18em] text-oth-ink3 border-b border-oth-line/70">
                                    <th className="py-3 pr-4 font-medium">Sub Category</th>
                                    <th className="py-3 pr-4 font-medium text-right">Allocation</th>
                                    <th className="py-3 font-medium">Amount</th>
                                </tr>
                            </thead>
                            <tbody>
                                {groups.map((g) => (
                                    <React.Fragment key={g.cat.id}>
                                        <tr data-testid={`inv-group-${g.cat.id}`}>
                                            <td colSpan={3} className="pt-4 pb-2">
                                                <div className="bg-[#eef3f8] border-l-4 border-oth-blue rounded-r-lg px-3 py-2 text-[12px] uppercase tracking-[0.22em] text-oth-blue font-body font-semibold">{g.cat.name}</div>
                                            </td>
                                        </tr>
                                        {g.items.map((r) => (
                                            <tr key={r.subcategory_id} className="border-b border-oth-line/40">
                                                <td className="py-3 pl-4 pr-4 text-oth-ink2">{r.subcategory_name}</td>
                                                <td className="py-3 pr-4 text-right tabular-nums" data-testid={`alloc-${r.subcategory_id}`}>{r.allocation.toFixed(1)}%</td>
                                                <td className="py-2.5 w-52">
                                                    <MoneyInput value={r.amount} onChange={(v) => setRow(r.subcategory_id, { amount: v })} testId={`amount-${r.subcategory_id}`} />
                                                </td>
                                            </tr>
                                        ))}
                                    </React.Fragment>
                                ))}
                            </tbody>
                            <tfoot>
                                <tr className="font-semibold">
                                    <td className="py-3.5 pr-4">Total</td>
                                    <td className="py-3.5 pr-4 text-right tabular-nums">100%</td>
                                    <td className="py-3.5 tabular-nums" data-testid="portfolio-total">{formatMoney(total)}</td>
                                </tr>
                            </tfoot>
                        </table>
                    </div>
                    <div className="mt-5 rounded-2xl bg-[#0B3B60] text-white px-6 py-4 flex items-center justify-between flex-wrap gap-2" data-testid="starting-wealth-banner">
                        <div className="font-body text-sm text-white/70">Your Starting Wealth — carried into the cash-flow projection</div>
                        <div className="font-display text-2xl font-semibold tracking-tight">{formatCompact(total)}</div>
                    </div>
                </div>
            )}
        </div>
    );
}
